import React, { useEffect, useMemo, useState } from "react";
import axios from "../../api/axios";
import { SaveButton } from "../buttons/Buttons";
import useAuth from "../../hooks/useAuth";

const WEBSITE_URL = "/websitejson";
const ART = "en";
const TITLE = "payment-settings";

const DEFAULT_SETTINGS = {
  enablePaymentGateway: false,
  stripeSecretKey: "",
  stripePublishableKey: "",
};

const toBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "off", ""].includes(normalized)) return false;
  }
  return Boolean(value);
};

const parsePaymentContent = (raw) => {
  if (!raw) return {};

  if (typeof raw === "object") return raw;

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return {};

    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object") return parsed;
      if (typeof parsed === "string") {
        const parsedTwice = JSON.parse(parsed);
        return parsedTwice && typeof parsedTwice === "object" ? parsedTwice : {};
      }
    } catch (err) {
      return {};
    }
  }

  return {};
};

const normalizeSettings = (content) => {
  const safe = parsePaymentContent(content);
  return {
    enablePaymentGateway: toBoolean(safe.enablePaymentGateway),
    stripeSecretKey: String(safe.stripeSecretKey || ""),
    stripePublishableKey: String(safe.stripePublishableKey || ""),
  };
};

export default function PaymentSettingsForm() {
  const { username } = useAuth();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [initialSettings, setInitialSettings] = useState(DEFAULT_SETTINGS);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(WEBSITE_URL, {
          params: { username, art: ART, title: TITLE },
        });

        const entry = response?.data?.entry || null;
        console.log("Fetched payment settings entry:", entry?.content);
        const parsed = normalizeSettings(entry?.content);
        setSettings(parsed);
        setInitialSettings(parsed);
      } catch (err) {
        console.log(err);
      }
    };

    if (username) {
      fetchSettings();
    }
  }, [username]);

  const canSave = useMemo(() => {
    const changed = JSON.stringify(settings) !== JSON.stringify(initialSettings);
    if (!changed) return false;

    if (!settings.enablePaymentGateway) return true;

    return Boolean(
      String(settings.stripeSecretKey || "").trim() &&
        String(settings.stripePublishableKey || "").trim()
    );
  }, [settings, initialSettings]);

  const onToggleEnableGateway = (checked) => {
    setSaveError("");
    setSettings((prev) => ({
      ...prev,
      enablePaymentGateway: checked,
    }));
  };

  const saveSettings = async (event) => {
    event.preventDefault();
    setSaveError("");
    setSaveSuccess(false);

    if (
      settings.enablePaymentGateway &&
      (!String(settings.stripeSecretKey || "").trim() ||
        !String(settings.stripePublishableKey || "").trim())
    ) {
      setSaveError("Please enter both Stripe Secret Key and Publishable Key.");
      return;
    }

    try {
      const payload = {
        username,
        art: ART,
        title: TITLE,
        content: {
          ...settings,
          stripeSecretKey: String(settings.stripeSecretKey || "").trim(),
          stripePublishableKey: String(settings.stripePublishableKey || "").trim(),
        },
      };

      await axios.put(WEBSITE_URL, payload);

      setInitialSettings(payload.content);
      setSettings(payload.content);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || "Failed to save payment settings.";
      setSaveError(message);
    }
  };

  return (
    <div>
      <form className="review-form payment-form">
        <h3>Payments</h3>

        <div className="payment-row">
          <label className="payment-checkbox">
            <input
              type="checkbox"
              checked={settings.enablePaymentGateway}
              onChange={(event) => onToggleEnableGateway(event.target.checked)}
            />
            Enable payment gateway for calendar bookings
          </label>
          <p className="payment-help-text">
            If disabled, all booking requests must be handled manually.
          </p>
        </div>

        {settings.enablePaymentGateway && (
          <>
            <div className="form-row">
              <label className="form-label" htmlFor="stripeSecretKey">
                Stripe Secret Key
              </label>
              <input
                id="stripeSecretKey"
                type="text"
                value={settings.stripeSecretKey}
                onChange={(event) =>
                  setSettings((prev) => ({ ...prev, stripeSecretKey: event.target.value }))
                }
                placeholder="sk_live_..."
              />
            </div>

            <div className="form-row">
              <label className="form-label" htmlFor="stripePublishableKey">
                Stripe Publishable Key
              </label>
              <input
                id="stripePublishableKey"
                type="text"
                value={settings.stripePublishableKey}
                onChange={(event) =>
                  setSettings((prev) => ({
                    ...prev,
                    stripePublishableKey: event.target.value,
                  }))
                }
                placeholder="pk_live_..."
              />
            </div>
          </>
        )}

        <div className="meta-form-actions">
          <SaveButton buttonText="Save" onClick={saveSettings} enabled={canSave} type="submit" />
        </div>

        {saveError && <p className="error-msg">{saveError}</p>}
        {saveSuccess && <p style={{ marginTop: "10px", color: "green" }}>Payment settings saved.</p>}
      </form>
    </div>
  );
}
